import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTextIcon, TrashIcon, UploadCloudIcon } from '@/utils/icons';
import { formatFileSize } from '@/utils/formatFileSize';
import styles from './UploadModal.module.css';

interface IFilePickerProps {
  file: File | null;
  disabled: boolean;
  /** The extension a pick must have (the file dialog's filter), '' for any. */
  accept: string;
  /** Both the input and the drop zone land here — first entry wins. */
  onPick: (files: FileList | null) => void;
  onClear: () => void;
}

/** The pick-a-file field: a drop zone until a file is staged, then a card
 *  showing it with a remove button. Owns the hidden input and the drag
 *  highlight; what to do with the picked file is the owner's business. */
export function FilePicker({ file, disabled, accept, onPick, onClear }: IFilePickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    onPick(e.dataTransfer.files);
  }

  function clear() {
    onClear();
    if (inputRef.current) inputRef.current.value = ''; // same file re-pickable
  }

  return (
    <div className={styles.field} data-tour="documents-upload-file">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className={styles.hiddenInput}
        onChange={(e) => onPick(e.target.files)}
      />
      {file ? (
        <div className={styles.fileCard}>
          <span className={styles.fileIcon} aria-hidden="true">
            <FileTextIcon size={20} />
          </span>
          <span className={styles.fileMeta}>
            <span className={styles.fileName}>{file.name}</span>
            <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
          </span>
          <button
            type="button"
            className={styles.removeBtn}
            onClick={clear}
            disabled={disabled}
            aria-label={t('documents.upload.removeFile')}
          >
            <TrashIcon size={16} />
          </button>
        </div>
      ) : (
        <div
          className={`${styles.dropZone} ${dragging ? styles.dropZoneDragging : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          <span className={styles.dropIcon} aria-hidden="true">
            <UploadCloudIcon size={26} />
          </span>
          <span className={styles.dropTitle}>{t('documents.upload.dropTitle')}</span>
          <span className={styles.dropHint}>{t('documents.upload.dropBrowse')}</span>
        </div>
      )}
    </div>
  );
}